import { Link } from "react-router-dom";
import { ArrowLeft, Zap } from "lucide-react";
import { BRAND, DISCORD_URL } from "@/lib/constants";
import { Footer } from "@/components/site/Footer";
import { useSeo } from "@/lib/seo";

const links = [
  { to: "/", label: "Home" },
  { to: "/#pricing", label: "Pricing" },
  { to: "/documentation", label: "Docs" },
  { to: "/referral", label: "Referrals" },
];

export default function NotFound() {
  useSeo({
    title: "Page Not Found — Flow Tweaks",
    description: "That page does not exist. Head back to Flow Tweaks for PC and console optimization.",
    path: "/404",
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="not-found-page">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {BRAND.name}
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4">Error 404</p>

        <h1
          className="text-6xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] mb-6"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Dropped <span className="text-orange-500">frames.</span>
        </h1>

        <p className="text-zinc-400 text-sm sm:text-base max-w-md mb-12 leading-relaxed">
          This page does not exist — bad link, old bookmark, or a typo in the URL. Nothing is
          broken with your account or your order.
        </p>

        <div className="flex flex-wrap gap-3 mb-12">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="border border-white/10 px-5 py-3 text-[11px] font-black uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-orange-500 hover:text-orange-500"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <p className="text-xs text-zinc-500 leading-relaxed">
          Looking for an order you already paid for? Open a ticket in our{" "}
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-500 underline underline-offset-4 hover:text-orange-400"
          >
            Discord
          </a>{" "}
          or email{" "}
          <a
            href="mailto:management@flowtweaks.com"
            className="text-orange-500 underline underline-offset-4 hover:text-orange-400"
          >
            management@flowtweaks.com
          </a>
          .
        </p>
      </main>

      <Footer />
    </div>
  );
}
